/**
 * 표 모델 코어 (P0-③) — 샘플 `block-editor.html`의 master-cell 그리드 모델 채택(D5).
 *
 * - `G`: 2차원 슬롯 배열. `G[r][c]`는 master 셀 객체의 **참조**. 병합 셀은 같은 참조가
 *   여러 슬롯을 채운다.
 * - master의 `r/c/rowspan/colspan`은 직접 쓰지 않고 `normalize(model)`가 G 분포에서
 *   매번 재계산한다(샘플 §3.1 불변 규칙).
 * - 직렬화 친화: master는 셀 inline content(BlockNote 배열)와 셀 props를 그대로 보관
 *   (DOM html 문자열 변환은 렌더 레이어 P3에서 inline.js로 처리).
 */

let uid = 0;

/** 새 master 셀. over로 content/props 초기화 가능. */
export function newMaster(over) {
  return { id: ++uid, content: [], r: 0, c: 0, rowspan: 1, colspan: 1, ...(over || {}) };
}

/** G 슬롯 분포로부터 각 master의 r/c/rowspan/colspan 재계산 */
export function normalize(model) {
  const G = model.G;
  const seen = new Map();
  for (let r = 0; r < G.length; r++) {
    for (let c = 0; c < G[r].length; c++) {
      const cell = G[r][c];
      if (!cell) continue;
      if (!seen.has(cell)) {
        seen.set(cell, { minR: r, minC: c, maxR: r, maxC: c });
      } else {
        const b = seen.get(cell);
        b.minR = Math.min(b.minR, r);
        b.minC = Math.min(b.minC, c);
        b.maxR = Math.max(b.maxR, r);
        b.maxC = Math.max(b.maxC, c);
      }
    }
  }
  seen.forEach((b, cell) => {
    cell.r = b.minR;
    cell.c = b.minC;
    cell.rowspan = b.maxR - b.minR + 1;
    cell.colspan = b.maxC - b.minC + 1;
  });
  return model;
}

/** 슬롯 그리드 G로 모델 생성(빈 슬롯은 새 master로 채움) + normalize */
export function newModelFromG(G, extra) {
  let cols = 0;
  G.forEach((r) => (cols = Math.max(cols, r.length)));
  G.forEach((r) => {
    for (let c = 0; c < cols; c++) if (!r[c]) r[c] = newMaster();
  });
  const model = { G, columnWidths: new Array(cols).fill(null), ...(extra || {}) };
  normalize(model);
  return model;
}

/** rows×cols 빈 표 모델 */
export function newTableModel(rows, cols) {
  const G = [];
  for (let r = 0; r < rows; r++) {
    const row = [];
    for (let c = 0; c < cols; c++) row.push(newMaster());
    G.push(row);
  }
  return newModelFromG(G);
}
